import {
  LookupAccountContext,
  MerchantCheckoutOpt,
  ModifyLoanContext,
  PerformAdditionalActionContext,
  ResumeFlowContext,
} from '@chargeafter/payment-types';
import {
  MerchantOpt,
  ModalMerchantOpt,
  ModifyLoanModalExtension,
  AccountLookupModalExtension,
  AdditionalActionModalExtension,
  ResumeFlowModalExtension,
} from '@chargeafter/types-sdk';
import { WidgetData } from '@chargeafter/types-widgets';

import { logger } from '../../../logger';
import { modalPresent } from '../../../manager';
import { OpenPaymentsModal } from '../../../manager/modal-manager/manager.model';
import { browserSessionId } from '../../../utils/browserSessionId';
import { convertMerchantOptToMerchantDetails } from '../../../utils/merchantDetailsMiddleware';
import {
  isMerchantOptValid,
  roundDecimalForCart,
} from '../../../validations/validations';
import { store } from '../../store';

type Flow =
  | 'apply'
  | 'checkout'
  | 'modifyLoan'
  | 'performAdditionalAction'
  | 'lookupAccount'
  | 'resume';

const isModifyLoan = (
  type: Flow,
  context?: MerchantOpt
): context is ModifyLoanContext => {
  return context && type === 'modifyLoan';
};

const isPerformAdditionalActionContext = (
  type: Flow,
  context?: MerchantOpt
): context is PerformAdditionalActionContext => {
  return context && type === 'performAdditionalAction';
};

const isLookupAccountContext = (
  type: Flow,
  context?: MerchantOpt
): context is LookupAccountContext => {
  return context && type === 'lookupAccount';
};

const isResumeFlowContext = (
  type: Flow,
  context?: MerchantOpt
): context is ResumeFlowContext => {
  return context && type === 'resume';
};

const filename = 'paymentsPresent';

export const paymentsPresent = async (
  type: Flow,
  opt: MerchantOpt & {
    widgetData?: WidgetData;
  } = {} as MerchantOpt
) => {
  const ttlStartTime = Date.now();

  const shouldRemoveApplicationId =
    opt &&
    type === 'apply' &&
    'applicationId' in opt &&
    opt.applicationId !== undefined &&
    opt.applicationId !== null;

  if (typeof opt !== 'object' || Array.isArray(opt)) {
    logger.error('invalid opt received, opt must be an object', {
      function: 'paymentsPresent',
      filename,
      printOriginalToConsole: true,
      opt,
    });
    return; // FIXME: should return error object;
  }

  if (shouldRemoveApplicationId) {
    logger.warn('applicationId cannot be sent in apply', {
      filename,
      function: 'applyPresent',
      printOriginalToConsole: true,
      isConsoleOnly: true,
    });
  }

  if (opt && type === 'checkout' && !opt['isCheckout']) {
    opt['isCheckout'] = true;
  }

  if (
    (type === 'apply' ||
      type === 'modifyLoan' ||
      type === 'performAdditionalAction' ||
      type === 'lookupAccount') &&
    opt &&
    (opt['isCheckout'] || typeof opt['isCheckout'] === 'undefined')
  ) {
    opt['isCheckout'] = false;
  }

  /**
   * Convert the ModifyLoan context to the intent object
   */
  if (isModifyLoan(type, opt)) {
    (opt['intent'] as ModifyLoanModalExtension['intent']) = {
      type: 'ModifyLoan',
    };
  } else if (isPerformAdditionalActionContext(type, opt)) {
    (opt['intent'] as AdditionalActionModalExtension['intent']) = {
      //Temporary, waiting for the intent name update from Core.
      type: 'ConsumerAdditionalAction',
    };
  } else if (isLookupAccountContext(type, opt)) {
    (opt['intent'] as AccountLookupModalExtension['intent']) = {
      type: 'LookupAccount',
      lenderId: opt.lenderId,
    };
    opt.lenderId = undefined;
  } else if (isResumeFlowContext(type, opt)) {
    (opt['intent'] as ResumeFlowModalExtension['intent']) = {
      type: 'Resume',
      lenderId: opt.lenderId,
    };
    opt.lenderId = undefined;
  } else {
    if (opt.consumerDetails?.mobilePhoneNumber) {
      opt.consumerDetails.mobilePhoneNumber =
        opt.consumerDetails.mobilePhoneNumber.toString().replace(/\D/g, '');
    }
  }

  if (!isMerchantOptValid(opt as MerchantCheckoutOpt)) {
    return void 0;
  } // FIXME: should return error object;

  if (type === 'checkout') {
    roundDecimalForCart((opt as MerchantCheckoutOpt).cartDetails);
  }

  const { onComplete } = store.config;

  const {
    callback,
    onDataUpdate,
    onConfirm,
    onApprovalStatusChange,
    onModalOpen,
    onApplicationCreated,
    widgetData,
    ...restOpt
  } = opt as MerchantCheckoutOpt & {
    widgetData?: WidgetData;
  };

  const isUpdateCallBackProvided = !!onDataUpdate;
  const isCallBackProvided = !!callback || !!onComplete;

  const initialMerchantOpt: ModalMerchantOpt = {
    ...restOpt,
    isUpdateCallBackProvided,
    isCallBackProvided,
    ...(shouldRemoveApplicationId && {
      applicationId: undefined,
    }),
  };

  const merchantOpt = convertMerchantOptToMerchantDetails(initialMerchantOpt);

  const payload: OpenPaymentsModal = {
    sendBrowserSessionStartedEvent:
      browserSessionId.sendBrowserSessionStartedEvent,
    browserSessionIdTimestamp: browserSessionId.timestamp,
    type: 'payments',
    paymentsData: { opt: merchantOpt },
    configurationSessionId: merchantOpt.configurationSessionId,
    ttlStartTime,
    ...(widgetData && {
      widget: {
        widgetData,
      },
    }),
  };

  const data =
    (await modalPresent({
      config: store.config,
      payload,
      isCheckout: type === 'checkout',
      merchantActions: {
        callback,
        onDataUpdate,
        onConfirm,
        onApprovalStatusChange,
        onModalOpen,
        onComplete,
        onApplicationCreated,
        onApplicationCreatedGlobal: store.config.onApplicationCreated,
      },
    })) || {};

  browserSessionId.sendBrowserSessionStartedEvent = false;

  return data;
};
